import { IBaseElementPayload, ISeatsPlan, ISectorElement, ISectorPayload } from '../interfaces';
import { Injectable } from '@angular/core';
import { FlattenSeat, GuidWrapper } from '../types';
import { CreatorStateService } from '../services/app-services';
import { ShapeAttributesManager } from './shape-attributes.manager';
import { Guid } from 'guid-typescript';
import { DEFAULT_SECTOR_ELEMENT_DATA } from '../constants';
import { ShapeKeys } from '../enums';
import { HTMLElementUtils } from '@cinetixx/cinetixx-ui';
import { TextFormattingUtils } from '../utils';

@Injectable()
export class SectorManager {

  public static getHTMLElement(guid: string, container?: HTMLElement): HTMLElement {
    return (container ?? CreatorStateService.roomRef.nativeElement).querySelector<HTMLElement>(
      `[${ShapeAttributesManager.getAttribute(ShapeKeys.GUID)}="${guid}"]`
    ) || null;
  }

  public static getDataToSend(seatsPlan: Partial<ISeatsPlan>): ISectorPayload {
    return {
      sectorId: seatsPlan?.id || DEFAULT_SECTOR_ELEMENT_DATA.sectorId,
      seatConfigurationId: seatsPlan?.seatConfigurationId || DEFAULT_SECTOR_ELEMENT_DATA.seatConfigurationId,
      parentSectorId: seatsPlan?.parentSectorId || null,
      hasSeats: seatsPlan?.flagHasSeats || false,
      zoomable: true,
      x: seatsPlan?.x || DEFAULT_SECTOR_ELEMENT_DATA.x,
      y: seatsPlan?.y || DEFAULT_SECTOR_ELEMENT_DATA.y,
      w: seatsPlan?.w || DEFAULT_SECTOR_ELEMENT_DATA.w,
      h: seatsPlan?.h || DEFAULT_SECTOR_ELEMENT_DATA.h,
      imageTypeId: null,
      noSeatSelection: seatsPlan?.noSeatSelection || false,
      webOpti: seatsPlan?.webOpti || false,
      allowSingleSeats: seatsPlan?.allowSingleSeats || false,
      isForNewCashApp: seatsPlan?.isForNewCashApp || false,
      backgroundColor: seatsPlan?.backgroundColor || null,
      backgroundImageId: seatsPlan?.backgroundImageId || null,
      name: seatsPlan?.name || '',
      shortName: seatsPlan?.shortName || '',
      hasSectors: seatsPlan?.hasSectors || false,
    };
  }

  public static getSectorElement(guid: string): GuidWrapper<ISectorElement> {
    return CreatorStateService.seatsPlan.sectors.find((seat) => seat.guid === guid) ?? null;
  }

  public static transformHTMLElementToObject(el: HTMLElement): GuidWrapper<ISectorElement> {
    const baseElement = CreatorStateService.seatsPlan.sectors.find(
      (bEl) => bEl.guid === ShapeAttributesManager.getGUID(el)
    );

    const shape = CreatorStateService.libraryShapes.find((s) => s.id === ShapeAttributesManager.getShapeId(el));

    const elData = {
      webX: parseInt(el.style.left),
      webY: parseInt(el.style.top),
      w: parseInt(el.style.width),
      h: parseInt(el.style.height),
      color: ShapeAttributesManager.getShapeBackgroundColor(el),
      shapeId: ShapeAttributesManager.getShapeId(el),
      rotate: HTMLElementUtils.getRotationDegrees(el.querySelector('.shape .editable-clip-path')),
      guid: ShapeAttributesManager.getGUID(el),
    } as GuidWrapper<ISectorElement>;

    if (baseElement) {
      return SectorManager.formatObject({
        ruleTitle: shape.name,
        ...baseElement,
        ...elData,
      });
    }

    return SectorManager.formatObject({
      ruleTitle: shape.name,
      ...elData
    });
  }

  public static getElementDataToSend(baseElement: Partial<ISectorElement>): IBaseElementPayload {
    const shape = CreatorStateService.shapes.find(x => x.id === baseElement.shapeId);

    return {
      ...SectorManager.getBaseObject(baseElement),
      webX: baseElement?.webX ?? DEFAULT_SECTOR_ELEMENT_DATA.webX,
      webY: baseElement?.webY ?? DEFAULT_SECTOR_ELEMENT_DATA.webY,
      x: baseElement?.x ?? DEFAULT_SECTOR_ELEMENT_DATA.x,
      y: baseElement?.y ?? DEFAULT_SECTOR_ELEMENT_DATA.y,
      width: baseElement?.w ?? DEFAULT_SECTOR_ELEMENT_DATA.w,
      height: baseElement?.h ?? DEFAULT_SECTOR_ELEMENT_DATA.h,
      color: baseElement?.color ?? DEFAULT_SECTOR_ELEMENT_DATA.color,
      flagSeatFac: DEFAULT_SECTOR_ELEMENT_DATA.flagSeatFac,
      flagRankFac: DEFAULT_SECTOR_ELEMENT_DATA.flagRankFac,
      title: TextFormattingUtils.formatText(baseElement?.ruleTitle, baseElement as FlattenSeat) || baseElement.ruleTitle || DEFAULT_SECTOR_ELEMENT_DATA.title,
      image: baseElement.image ?? shape.image,
      imageX: baseElement.imageX ?? shape.imageX,
      imageY: baseElement.imageY ?? shape.imageY,
      shapeId: baseElement?.shapeId || DEFAULT_SECTOR_ELEMENT_DATA.shapeId,
      alignCurveId: baseElement?.alignCurveId || DEFAULT_SECTOR_ELEMENT_DATA.alignCurveId,
      sectorElementId: baseElement?.id ?? null,
      ruleTitle: baseElement.ruleTitle || DEFAULT_SECTOR_ELEMENT_DATA.ruleTitle,
      ruleTopText: baseElement.ruleTopText || DEFAULT_SECTOR_ELEMENT_DATA.ruleTopText,
      bottomText: TextFormattingUtils.formatText(baseElement?.bottomText, baseElement as FlattenSeat) || DEFAULT_SECTOR_ELEMENT_DATA.bottomText,
      ruleBottomText: baseElement.ruleBottomText || DEFAULT_SECTOR_ELEMENT_DATA.ruleBottomText,
      tipText: TextFormattingUtils.formatText(baseElement?.tipText, baseElement as FlattenSeat) || DEFAULT_SECTOR_ELEMENT_DATA.tipText,
      ruleTipText: baseElement.ruleTipText || DEFAULT_SECTOR_ELEMENT_DATA.ruleTipText,
      topText: TextFormattingUtils.formatText(baseElement?.topText, baseElement as FlattenSeat) || DEFAULT_SECTOR_ELEMENT_DATA.topText,
    } as IBaseElementPayload;
  }

  public static formatObject(el: Partial<GuidWrapper<ISectorElement>>): GuidWrapper<ISectorElement> {
    return {
      ...SectorManager.getBaseObject(el),
      guid: el.guid ?? Guid.raw(),
      webX: el.webX ?? DEFAULT_SECTOR_ELEMENT_DATA.webX,
      webY: el.webY ?? DEFAULT_SECTOR_ELEMENT_DATA.webY,
      w: el.w ?? DEFAULT_SECTOR_ELEMENT_DATA.w,
      h: el.h ?? DEFAULT_SECTOR_ELEMENT_DATA.h,
      title: el.title ?? DEFAULT_SECTOR_ELEMENT_DATA.title,
      color: el.color ?? DEFAULT_SECTOR_ELEMENT_DATA.color,
      flagRankFac: el.flagRankFac ?? DEFAULT_SECTOR_ELEMENT_DATA.flagRankFac,
      flagSeatFac: el.flagSeatFac ?? DEFAULT_SECTOR_ELEMENT_DATA.flagSeatFac,
      image: el.image ?? DEFAULT_SECTOR_ELEMENT_DATA.image,
      imageX: el.imageX ?? DEFAULT_SECTOR_ELEMENT_DATA.imageX,
      imageY: el.imageY ?? DEFAULT_SECTOR_ELEMENT_DATA.imageY,
      id: el.id ?? DEFAULT_SECTOR_ELEMENT_DATA.id,
      topText: el.topText ?? DEFAULT_SECTOR_ELEMENT_DATA.topText,
      bottomText: el.bottomText ?? DEFAULT_SECTOR_ELEMENT_DATA.bottomText,
      ruleTipText: el.ruleTipText ?? DEFAULT_SECTOR_ELEMENT_DATA.ruleTipText,
      ruleTitle: el.ruleTitle ?? DEFAULT_SECTOR_ELEMENT_DATA.ruleTitle,
      ruleTopText: el.ruleTopText ?? DEFAULT_SECTOR_ELEMENT_DATA.ruleTopText,
      ruleBottomText: el.ruleBottomText ?? DEFAULT_SECTOR_ELEMENT_DATA.ruleBottomText,
      tipText: el.tipText ?? DEFAULT_SECTOR_ELEMENT_DATA.tipText,
    } as GuidWrapper<ISectorElement>;
  }

  private static getBaseObject(el: Partial<GuidWrapper<ISectorElement>>): Partial<GuidWrapper<ISectorElement>> {
    return {
      titleSizeMax: el.titleSizeMax ?? DEFAULT_SECTOR_ELEMENT_DATA.titleSizeMax,
      titleSizeMin: el.titleSizeMin ?? DEFAULT_SECTOR_ELEMENT_DATA.titleSizeMin,
      shapeId: el.shapeId ?? DEFAULT_SECTOR_ELEMENT_DATA.shapeId,
      rotate: el.rotate ?? DEFAULT_SECTOR_ELEMENT_DATA.rotate,
      alignCurveId: el.alignCurveId ?? DEFAULT_SECTOR_ELEMENT_DATA.alignCurveId,
      alignCurvePosition: el.alignCurvePosition ?? DEFAULT_SECTOR_ELEMENT_DATA.alignCurvePosition,
      seatConfigurationId: el.seatConfigurationId ?? CreatorStateService.seatsPlan.seatConfigurationId,
      sectorId: el.sectorId ?? DEFAULT_SECTOR_ELEMENT_DATA.sectorId,
    };
  }
}
